import { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import api from "../lib/api";

const inputCls =
  "mt-2 w-full rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none";

const AdminPasswordChange = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("The two new passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — use it next time you sign in.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not change the password — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="h-fit rounded-3xl border border-line bg-white p-8" data-testid="admin-password-card">
      <h3 className="flex items-center gap-2 font-display text-2xl font-medium text-ink">
        <KeyRound className="h-5 w-5 text-marmalade" />
        Change password
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-inkSoft">
        Pick something only you know — you'll use it at the sitter login.
      </p>
      <label className="mt-6 block">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Current password</span>
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          data-testid="admin-password-current"
          className={inputCls}
          required
        />
      </label>
      <label className="mt-4 block">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">New password (min 8 characters)</span>
        <input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          data-testid="admin-password-new"
          className={inputCls}
          required
        />
      </label>
      <label className="mt-4 block">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Repeat new password</span>
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid="admin-password-confirm"
          className={inputCls}
          required
        />
      </label>
      {error && (
        <p className="mt-3 text-xs text-red-600" data-testid="admin-password-error">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={saving}
        data-testid="admin-password-save"
        className="mt-6 w-full rounded-full bg-forest py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-ink disabled:opacity-60"
      >
        {saving ? "Saving…" : "Update password"}
      </button>
    </form>
  );
};

export default AdminPasswordChange;
